import { ChatStore } from "@/src/store";
import type {
  SenderSendResult,
  SenderSubmitOptions,
} from "@/src/components/Sender/type";
import { useMemoizedFn } from "ahooks";

interface UseSenderMessageProps {
  onSend: (
    message: string,
    options?: SenderSubmitOptions,
  ) => Promise<SenderSendResult | unknown> | SenderSendResult | unknown;
  clearAndFocusInput: () => void;
  clearAttachments?: () => void;
  clearVoiceContext?: () => void;
  hideDeepSearch?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

interface UseSenderMessageResult {
  handleSend: (message: string, options?: SenderSubmitOptions) => void;
  toggleDeepSearch: () => void;
}

/**
 * Custom hook to handle message sending logic including permission checks and error handling
 */
export const useSenderMessage = ({
  onSend,
  clearAndFocusInput,
  clearAttachments,
  clearVoiceContext,
  disabled = false,
  loading = false,
}: UseSenderMessageProps): UseSenderMessageResult => {
  const handleSend = useMemoizedFn(
    async (inputStr: string, options?: SenderSubmitOptions) => {
      if (disabled || loading) {
        return;
      }
      const hasText = !!inputStr?.trim();
      // 统一发送规则：必须有文字输入（trim 后非空）；附件仅作为随文字一起发送的附加信息
      if (!hasText) {
        return;
      }
      const result = await Promise.resolve(onSend(inputStr || "", options));
      const resolved: SenderSendResult | undefined =
        result && typeof result === "object" && "sent" in result
          ? (result as SenderSendResult)
          : undefined;

      // Back-compat: if no result is returned, assume sent.
      const sent = resolved ? resolved.sent : true;
      if (!sent) {
        return;
      }
      clearAndFocusInput();
      clearAttachments?.();
      clearVoiceContext?.();
    },
  );

  const toggleDeepSearch = useMemoizedFn(async () => {
    if (disabled || loading) {
      return;
    }
    // Directly toggle the ChatStore state
    ChatStore.isDeepsearch = !ChatStore.isDeepsearch;
  });

  return {
    handleSend,
    toggleDeepSearch,
  };
};
